import React, { useState, useRef, useEffect, useCallback } from 'react';
import { parseUtc } from '../../utils/dataAge';

interface CommandEntry {
  id: number;
  command: string;
  response: string | null;
  sent_at: string;   // ISO, UTC (see utils/dataAge.parseUtc)
  ms: number | null;
  note?: string | null;   // transient — only present on the just-sent command's own response
}

interface SavedCommand {
  label: string;
  cmd: string;
}

interface Props {
  deviceId: number;
}

const DEFAULT_SAVED: SavedCommand[] = [
  { label: 'getver - wersja firmware', cmd: 'getver' },
  { label: 'getinfo - informacje o urządzeniu', cmd: 'getinfo' },
  { label: 'getstatus - status urządzenia', cmd: 'getstatus' },
];

const HISTORY_PAGE = 10;
const TIMEOUT_S = 600;      // 10 min — matches Horta batch-mode reconnect window (~2.5 min)
const POLL_MS = 5000;       // re-fetch while something is pending, to catch delayed acks

const loadSaved = (): SavedCommand[] => {
  try {
    const raw = localStorage.getItem('saved_commands');
    if (raw === null) return DEFAULT_SAVED;
    const stored = JSON.parse(raw);
    return Array.isArray(stored) ? stored : DEFAULT_SAVED;
  } catch { return DEFAULT_SAVED; }
};

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

type CmdStatus = 'pending' | 'ok' | 'info' | 'error' | 'timeout';

// Backend/gateway embed a non-zero 0x88 ack.status as "[status=N] <text>" in
// the same `response` text column (see commands.py:_format_command_response
// / horta_gateway.py:_on_command_ack) — status!=0 doesn't necessarily mean
// the command failed (e.g. RECINFO's "Ovr" flag), so only treat it as a red
// error when the device's own text looks like one.
const STATUS_PREFIX_RE = /^\[status=(\d+)\]\s*/;
const ERROR_TEXT_RE = /^(invalid|missing|error)/i;

interface DerivedStatus {
  status: CmdStatus;
  elapsedSec: number;
  statusCode?: number;
}

function deriveStatus(response: string | null, sentAtIso: string, nowMs: number): DerivedStatus {
  const elapsedSec = Math.floor((nowMs - parseUtc(sentAtIso).getTime()) / 1000);
  if (response == null) {
    return { status: elapsedSec >= TIMEOUT_S ? 'timeout' : 'pending', elapsedSec };
  }
  // Local-only synthetic entries for a failed fetch/non-2xx POST (see send())
  // — a real client/network error, not a device 0x88 reply, always red.
  if (response.startsWith('BŁĄD: ')) {
    return { status: 'error', elapsedSec };
  }
  const m = response.match(STATUS_PREFIX_RE);
  if (m) {
    const statusCode = parseInt(m[1], 10);
    const rest = response.slice(m[0].length).trim();
    if (!rest || ERROR_TEXT_RE.test(rest)) {
      return { status: 'error', elapsedSec, statusCode };
    }
    return { status: 'info', elapsedSec, statusCode };
  }
  return { status: 'ok', elapsedSec };
}

const STATUS_LABEL: Record<'pending' | 'ok' | 'error' | 'timeout', string> = {
  pending: 'oczekuje',
  ok: 'odpowiedź',
  error: 'błąd',
  timeout: 'timeout',
};

const StatusBadge = ({ response, sentAt, now }: { response: string | null; sentAt: string; now: number }) => {
  const { status, elapsedSec, statusCode } = deriveStatus(response, sentAt, now);
  const label = status === 'pending' ? `oczekuje ${elapsedSec}s`
    : status === 'info' ? `odpowiedź (status ${statusCode})`
    : STATUS_LABEL[status];
  return <span className={`cmd-badge cmd-badge--${status}`}>{label}</span>;
};

const CommandPanel: React.FC<Props> = ({ deviceId }) => {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<CommandEntry[]>([]);
  const [visibleCount, setVisibleCount] = useState(HISTORY_PAGE);
  const [savedCmds, setSavedCmds] = useState<SavedCommand[]>(loadSaved);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [now, setNow] = useState(() => Date.now());

  const dropdownAreaRef = useRef<HTMLDivElement>(null);

  const fetchHistory = useCallback(() => {
    return fetch(`/api/devices/${deviceId}/commands/today`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then((data: CommandEntry[]) => setHistory(data))
      .catch(() => {});
  }, [deviceId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Live "oczekuje Xs" counter + polling while anything is still pending
  // (response===null and age<10min) — catches a Horta batch-mode device's
  // delayed reply after it reconnects, without a manual refresh.
  const hasPending = history.some(h => deriveStatus(h.response, h.sent_at, now).status === 'pending');
  useEffect(() => {
    if (!hasPending) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(fetchHistory, POLL_MS);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [hasPending, fetchHistory]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownAreaRef.current && !dropdownAreaRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  const persistSaved = (cmds: SavedCommand[]) => {
    setSavedCmds(cmds);
    localStorage.setItem('saved_commands', JSON.stringify(cmds));
  };

  const addSaved = () => {
    if (!input.trim() || !newLabel.trim()) return;
    persistSaved([...savedCmds, { label: newLabel.trim(), cmd: input.trim() }]);
    setNewLabel('');
  };

  const removeSaved = (idx: number) => {
    persistSaved(savedCmds.filter((_, i) => i !== idx));
  };

  const send = async (cmd: string) => {
    if (!cmd.trim() || loading) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/devices/${deviceId}/commands`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ command: cmd.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        setHistory(prev => [data, ...prev]);
        setNow(Date.now());
        setInput('');
      } else {
        setHistory(prev => [{
          id: -Date.now(), command: cmd.trim(), response: `BŁĄD: ${data.detail || res.status}`,
          sent_at: new Date().toISOString(), ms: null,
        }, ...prev]);
      }
    } catch {
      setHistory(prev => [{
        id: -Date.now(), command: cmd.trim(), response: 'BŁĄD: Błąd połączenia',
        sent_at: new Date().toISOString(), ms: null,
      }, ...prev]);
    } finally {
      setLoading(false);
    }
  };

  const visible = history.slice(0, visibleCount);

  return (
    <div className="cmd-panel">
      <div className="cmd-history">
        {history.length === 0 && (
          <div className="cmd-empty">Brak historii komend</div>
        )}
        {visible.map((h) => {
          const { status } = deriveStatus(h.response, h.sent_at, now);
          const displayResponse = h.response == null ? null
            : h.response.startsWith('BŁĄD: ') ? h.response.slice('BŁĄD: '.length).trim()
            : h.response.replace(STATUS_PREFIX_RE, '') || null;
          return (
            <div key={h.id} className="cmd-history-row">
              <div className="cmd-history-top">
                <span className="cmd-history-cmd">{h.command}</span>
                <span className="cmd-history-time">{parseUtc(h.sent_at).toLocaleTimeString('pl-PL')}</span>
                <StatusBadge response={h.response} sentAt={h.sent_at} now={now} />
              </div>
              {h.note && <div className="cmd-history-note">{h.note}</div>}
              {displayResponse != null && (
                <pre className={`cmd-history-response${status === 'error' ? ' cmd-history-response--error' : ''}`}>
                  {displayResponse}
                </pre>
              )}
            </div>
          );
        })}
        {history.length > visibleCount && (
          <button className="cmd-show-more" onClick={() => setVisibleCount(v => v + HISTORY_PAGE)}>
            Pokaż więcej ({history.length - visibleCount})
          </button>
        )}
      </div>

      <div className="cmd-input-area" ref={dropdownAreaRef}>
        {dropdownOpen && (
          <div className="cmd-dropdown">
            <div className="cmd-dropdown-list">
              {savedCmds.length === 0 && (
                <div className="cmd-dropdown-empty">Brak zapisanych komend</div>
              )}
              {savedCmds.map((sc, i) => (
                <div key={i} className="cmd-dropdown-item">
                  <button
                    className="cmd-dropdown-cmd"
                    onClick={() => { setInput(sc.cmd); setDropdownOpen(false); }}
                  >
                    <span className="cmd-dropdown-label">{sc.label}</span>
                    <span className="cmd-dropdown-preview">{sc.cmd}</span>
                  </button>
                  <button className="cmd-dropdown-del" onClick={() => removeSaved(i)} title="Usuń">×</button>
                </div>
              ))}
            </div>
            <div className="cmd-dropdown-add">
              <input
                className="cmd-dropdown-label-input"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addSaved()}
                placeholder={input.trim() ? `Etykieta dla „${input.trim()}"…` : 'Wpisz komendę w polu poniżej'}
                disabled={!input.trim()}
              />
              <button
                className="cmd-dropdown-add-btn"
                onClick={addSaved}
                disabled={!input.trim() || !newLabel.trim()}
              >
                Dodaj
              </button>
            </div>
          </div>
        )}

        <div className="cmd-input-row">
          <button
            className={`cmd-presets-btn${dropdownOpen ? ' active' : ''}`}
            onClick={() => setDropdownOpen(v => !v)}
            title="Zapisane komendy"
          >📋</button>
          <input
            className="cmd-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && send(input)}
            placeholder="Wpisz komendę..."
            disabled={loading}
          />
          <button className="cmd-send" onClick={() => send(input)} disabled={loading || !input.trim()}>
            {loading ? '⏳' : 'Wyślij'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommandPanel;
